import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { listStaff } from "../../api/staffApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listGroupConfigurationsByClassId } from "../../api/groupConfigurationApi";
import { listSubjectConfigurations } from "../../api/subjectConfigurationApi";
import { createLookupApi } from "../../api/lookupApi";
import {
  listSubjectAssignToTeachers,
  assignSubjectToTeacher,
  deleteSubjectAssignToTeacher,
  deleteAllSubjectAssignToTeacherForStaff,
} from "../../api/subjectAssignToTeacherApi";
import "../Attendance/Attendance.css";
import "../Users/UsersPage.css";

const subjectApi = createLookupApi("subjects");

/**
 * Direct port of digicampusbd's `staff-assign-subject-teacher` ("Subject
 * Assign") — new `SubjectAssignToTeacher` entity/repository/service/
 * controller (a real net-new join table, same class as the already-built
 * `ClassTeacherAssign`, just three-way: staff × section × subject — no
 * group column at all; "group" is purely a real-app UI filter for narrowing
 * the subject checklist, not part of what gets saved).
 * Documented simplification: the old app's section dropdown is scoped to
 * only sections that teacher is already a class-teacher for — a UX
 * convenience filter, not business logic; this port accepts any real
 * section directly instead.
 *
 * Carries the old app's real "Select Group" filter (previously missing
 * here — this page originally shipped before `SubjectConfiguration` existed
 * in this migration, so every institute subject was offered unconditionally
 * instead): picking a group narrows the subject checklist to that
 * class+group's real `SubjectConfiguration` rows, same pattern as
 * `SubjectConfigurationPage`/`ExamMarkBlankSheetPage` — falling back to
 * every subject when no group is picked or the group has no Subject Setup
 * rows at all. Also wires up the old app's real per-teacher "All Delete"
 * bulk action (`DELETE /subject-assign-to-teacher/staff/{staffId}` — already
 * built on the backend, never called from this page before).
 */
export default function SubjectAssignToTeacherPage() {
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [allSubjects, setAllSubjects] = useState([]);
  const [groupConfiguredSubjectIds, setGroupConfiguredSubjectIds] = useState(null);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [assigns, setAssigns] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadAssigns = () => listSubjectAssignToTeachers().then(setAssigns);

  useEffect(() => {
    listStaff().then((list) => {
      setStaffList(list);
      if (list.length > 0) setStaffId(String(list[0].staffId));
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
    subjectApi.list().then(setAllSubjects);
    loadAssigns();
  }, []);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigId);
    setGroupId("");
    setGroups([]);
    if (cc) {
      listGroupConfigurationsByClassId(cc.classId).then(setGroups);
    }
  }, [classConfigId, classConfigs]);

  useEffect(() => {
    const cc = classConfigs.find((c) => String(c.id) === classConfigId);
    if (!cc || !groupId) {
      setGroupConfiguredSubjectIds(null);
      return;
    }
    let cancelled = false;
    listSubjectConfigurations(cc.classId, groupId).then((rows) => {
      if (cancelled) return;
      setGroupConfiguredSubjectIds(new Set(rows.map((r) => String(r.subjectId))));
    });
    return () => {
      cancelled = true;
    };
  }, [groupId, classConfigId, classConfigs]);

  const subjects =
    groupConfiguredSubjectIds === null || groupConfiguredSubjectIds.size === 0
      ? allSubjects
      : allSubjects.filter((s) => groupConfiguredSubjectIds.has(String(s.id)));

  useEffect(() => {
    const visibleIds = new Set(subjects.map((s) => String(s.id)));
    setSelectedSubjectIds((prev) => prev.filter((id) => visibleIds.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupConfiguredSubjectIds]);

  const toggleSubject = (id) => setSelectedSubjectIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleAssign = async () => {
    setError("");
    setSuccess("");
    if (!staffId || !classConfigId || selectedSubjectIds.length === 0) {
      setError("Please select a staff member, a section, and at least one subject.");
      return;
    }
    try {
      await assignSubjectToTeacher(staffId, classConfigId, selectedSubjectIds);
      setSuccess("Assigned.");
      setSelectedSubjectIds([]);
      loadAssigns();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to assign.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSubjectAssignToTeacher(id);
      loadAssigns();
    } catch (err) {
      setError("Failed to remove assignment.");
    }
  };

  const handleDeleteAll = async (staffIdToDelete, staffName) => {
    if (!window.confirm(`Remove every subject assignment for "${staffName}"?`)) return;
    try {
      await deleteAllSubjectAssignToTeacherForStaff(staffIdToDelete);
      loadAssigns();
    } catch (err) {
      setError("Failed to remove all assignments for this teacher.");
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Subject Assign</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {success && <p style={{ color: "var(--color-success, green)" }}>{success}</p>}
      <div className="attendance-filters">
        <label>
          Staff
          <select value={staffId} onChange={(e) => setStaffId(e.target.value)}>
            {staffList.map((s) => (
              <option key={s.staffId} value={s.staffId}>
                {s.staffName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Group
          <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">All groups</option>
            {groups.map((g) => (
              <option key={g.groupId} value={g.groupId}>
                {g.groupName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleAssign}>
          Assign
        </button>
      </div>
      <div className="form-row">
        <span className="admit-card-label">Subjects:</span>{" "}
        {subjects.map((s) => (
          <label key={s.id} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
            <input type="checkbox" checked={selectedSubjectIds.includes(String(s.id))} onChange={() => toggleSubject(String(s.id))} />
            {s.name}
          </label>
        ))}
      </div>
      <div className="table-scroll">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Staff</th>
              <th>Section</th>
              <th>Subject</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {assigns.map((a) => (
              <tr key={a.id}>
                <td>{a.staffName}</td>
                <td>{a.classConfigName}</td>
                <td>{a.subjectName}</td>
                <td className="actions-col">
                  <button type="button" className="btn-link danger" onClick={() => handleDelete(a.id)}>
                    Remove
                  </button>
                  <button type="button" className="btn-link danger" onClick={() => handleDeleteAll(a.staffId, a.staffName)}>
                    Remove All (this teacher)
                  </button>
                </td>
              </tr>
            ))}
            {assigns.length === 0 && (
              <tr>
                <td colSpan={4}>No subject assignments yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
}
